import axios from 'axios';
import PropTypes from 'prop-types';
import { useState } from 'react';

import AuthenticationStore from '~/auth/authenticationStore';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Logo } from '~/eds/src/components/Logo/Logo';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { intlService } from '~/i18n/IntlService';
import Configuration from '~/services/configuration';

const EmailApproveViewType = {
  Input: 'INPUT',
  Approved: 'APPROVED',
  InvalidLink: 'INVALID_LINK',
};

const AS = AuthenticationStore();

function InputView({
  comment,
  setComment,
  handleSendApproveRequest,
  viewTicket,
}) {
  return (
    <Layout align="center" direction="column" spacing={6}>
      <Text as="h1" variant="subtitle">
        {intlService.getIntl().formatMessage({
          id: 'judgmentResults.approveConfirmationText',
          defaultMessage: 'Are you sure you want to approve this item?',
          description: 'Confirmation question when approving a judgment result',
        })}
      </Text>
      <Box w="60%">
        <Layout direction="column" spacing={6}>
          <TextArea
            name="approve-comment-textarea"
            placeholder={intlService.getIntl().formatMessage({
              id: 'judgmentResults.approvedFromEmailPlaceholder',
              defaultMessage: 'Approved from email',
              description: 'Placeholder text for approval comment textarea',
            })}
            rows={6}
            maxLength="4096"
            value={comment}
            onChange={setComment}
          />
          <Layout justify="flex-end" spacing={6}>
            <Layout preset="buttons">
              <Button
                variant="secondary"
                name="view-ticket-button"
                onClick={viewTicket}
                text={intlService.getIntl().formatMessage({
                  id: 'judgmentResults.viewTicketButton',
                  defaultMessage: 'View Ticket',
                  description: 'Button text to view ticket',
                })}
              ></Button>
              <Button
                variant="primary"
                name="action-button"
                onClick={handleSendApproveRequest}
                text={intlService.getIntl().formatMessage({
                  id: 'judgmentResults.approveTicketButton',
                  defaultMessage: 'Approve Ticket',
                  description: 'Button text to approve ticket',
                })}
              ></Button>
            </Layout>
          </Layout>
        </Layout>
      </Box>
    </Layout>
  );
}

InputView.propTypes = {
  comment: PropTypes.string.isRequired,
  setComment: PropTypes.func.isRequired,
  handleSendApproveRequest: PropTypes.func.isRequired,
  viewTicket: PropTypes.func.isRequired,
};

function ErrorMessageView() {
  return (
    <Layout align="center" direction="column">
      <Text as="h1" variant="subtitle">
        {intlService.getIntl().formatMessage({
          id: 'ticketJudgmentResult.invalidLink',
          defaultMessage: 'Invalid link',
          description: 'Error message when ticket judgment link is invalid',
        })}
      </Text>
    </Layout>
  );
}

function ApproveMessageView() {
  return (
    <Layout align="center" direction="column">
      <Text as="h1" variant="subtitle">
        {intlService.getIntl().formatMessage({
          id: 'ticketJudgmentResult.ticketApproved',
          defaultMessage: 'Ticket approved',
          description: 'Success message when ticket has been approved',
        })}
      </Text>
    </Layout>
  );
}

function Page() {
  const isLoggedIn = AS.isLoggedIn();

  const [comment, setComment] = useState();
  const [view, setView] = useState();

  const formData = new FormData();
  formData.append('payload', JSON.stringify({ comment: comment }));

  const stringArray = window.location.pathname.split('/');
  const [_emptyItem, clientSubDomain, _tickets, ticketId, ...otherElements] =
    stringArray;
  const actionUrl = `${
    Configuration.everestEndpoint
  }/tickets/${ticketId}/${otherElements.join('/')}`;
  const ticketUrl = `${window.location.origin}/${clientSubDomain}/workflow/tickets/${ticketId}/`;

  const handleSendApproveRequest = async () => {
    try {
      await axios.patch(actionUrl, formData, 'Content-Type');
      setView(EmailApproveViewType.Approved);
    } catch {
      setView(EmailApproveViewType.InvalidLink);
    }
  };

  const viewTicket = () => {
    window.location.href = ticketUrl;
  };

  function renderView() {
    switch (view) {
      case EmailApproveViewType.Approved:
        return <ApproveMessageView />;
      case EmailApproveViewType.InvalidLink:
        return <ErrorMessageView />;
      default:
        return (
          <InputView
            comment={comment}
            setComment={setComment}
            handleSendApproveRequest={handleSendApproveRequest}
            viewTicket={viewTicket}
          />
        );
    }
  }

  return (
    <Layout
      as="header"
      direction="column"
      spacing={16}
      pt={isLoggedIn ? 16 : 0}
    >
      {isLoggedIn ? null : (
        <Layout
          align="center"
          bg="inverse.background"
          h="header.height"
          justify="space-between"
          px="6"
        >
          <a href="/" title="Evisort login page">
            <Logo theme="dark" />
          </a>
        </Layout>
      )}
      {renderView()}
    </Layout>
  );
}

export default Page;
